import { useState, useEffect } from 'react';
import { supabase } from './supabaseClient';
import { useNavigate } from 'react-router-dom';
// Rimuovi l'importazione del CSS originale
// import './AggiornaProfilo.css';
import './tailwind.css';

export default function AggiornaProfilo() {
  const [formData, setFormData] = useState({
    nome: '',
    cognome: '',
    email: '',
    password: ''
  });

  const [message, setMessage] = useState('');
  const navigate = useNavigate(); // Inizializza useNavigate

  useEffect(() => {
    const loadData = async () => {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      const userId = sessionData?.session?.user?.id;

      // Se non c'è una sessione utente valida, reindirizza al login
      if (sessionError || !userId) {
        console.error("Nessuna sessione trovata, reindirizzo al login:", sessionError?.message);
        navigate('/');
        return;
      }

      const { data: utente, error: userProfileError } = await supabase
        .from('utenti')
        .select('nome, cognome, email')
        .eq('id', userId)
        .single();

      if (userProfileError) {
        console.error("Errore nel caricamento del profilo utente:", userProfileError.message);
        setMessage('Errore nel caricamento dei dati del profilo.');
        // Potresti voler reindirizzare o mostrare un messaggio più specifico
        return;
      }

      if (utente) {
        setFormData({
          nome: utente.nome,
          cognome: utente.cognome,
          email: utente.email,
          password: ''
        });
      } else {
        setMessage('Profilo utente non trovato.');
        // Potresti voler reindirizzare l'utente o richiedere una nuova autenticazione
      }
    };

    loadData();
    // Aggiungi navigate alle dipendenze per l'useEffect
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const { nome, cognome, password } = formData;
    setMessage(''); // Pulisci i messaggi precedenti

    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) {
      setMessage('Errore: Utente non autenticato. Riprova il login.');
      return;
    }

    // Aggiorna nome e cognome nella tabella 'utenti'
    const { error: utentiError } = await supabase
      .from('utenti')
      .update({ nome, cognome })
      .eq('id', userId);

    if (utentiError) {
      setMessage('Errore nell\'aggiornamento del profilo: ' + utentiError.message);
      return;
    }

    // Se è stata fornita una nuova password, aggiornala nel sistema di autenticazione
    if (password) {
      if (password.length < 6) {
        setMessage('La nuova password deve essere di almeno 6 caratteri.');
        return;
      }
      const { error: passError } = await supabase.auth.updateUser({ password });
      if (passError) {
        setMessage('Errore nell\'aggiornamento della password: ' + passError.message);
        return;
      }
    }

    setMessage('Profilo aggiornato con successo.');
  };

  return (
    // Contenitore principale della pagina (AggiornaProfilo):
    // Questo div ora sarà la "scatola bianca" che occupa l'intera area disponibile del suo contenitore padre (main della dashboard).
    // Aggiunto h-full per occupare tutta l'altezza, bg-white, padding, bordi arrotondati e ombra.
    // Il testo è centrato e gli elementi interni sono impostati con flex per il layout.
    <div className="w-full h-full bg-white p-6 md:p-8 rounded-lg shadow-xl text-gray-800 flex flex-col items-center overflow-y-auto">
      {/* Questo div interno limita la larghezza del contenuto effettivo (h1, p, form)
          e lo centra orizzontalmente con mx-auto. */}
      <div className="max-w-lg w-full">
        <h1 className="text-2xl md:text-3xl font-bold uppercase mb-4 text-center text-mioblu">
          Aggiorna il tuo profilo
        </h1>
        <p className="text-mioblu mb-6 text-sm text-center">
          Modifica i tuoi dati personali qui sotto. Lascia il campo password vuoto se non vuoi cambiarla.
        </p>

        <form onSubmit={handleSave} className="flex flex-col gap-4">
          {/* Rimossi i blocchi condizionali per 'error' e 'success' in quanto gestiti da 'message' */}
          {/* Le variabili di stato 'error' e 'success' sono state rimosse in quanto non utilizzate */}

          <label htmlFor="nome" className="block text-sm font-medium text-mioblu">Nome:</label>
          <input
            type="text"
            id="nome"
            name="nome"
            placeholder="Nome"
            value={formData.nome}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-800"
          />

          <label htmlFor="cognome" className="block text-sm font-medium text-mioblu">Cognome:</label>
          <input
            type="text"
            id="cognome"
            name="cognome"
            placeholder="Cognome"
            value={formData.cognome}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-800"
          />

          <label htmlFor="email" className="block text-sm font-medium text-mioblu">Email:</label>
          <input
            type="email"
            id="email"
            name="email"
            placeholder="Email"
            value={formData.email}
            disabled
            className="w-full px-4 py-2 border border-gray-300 rounded-md bg-gray-100 text-gray-500 cursor-not-allowed focus:outline-none"
          />
          <small className="text-xs text-miorosso -mt-2">
            L'indirizzo email non può essere modificato. Per cambiarlo, contatta l'amministratore.
          </small>

          <label htmlFor="password" className="block text-sm font-medium text-mioblu">Nuova Password:</label>
          <input
            type="password"
            id="password"
            name="password"
            placeholder="Lascia vuoto per non cambiare"
            value={formData.password}
            onChange={handleChange}
            className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent text-gray-800"
          />

          <button
            type="submit"
            className="w-full px-4 py-2 bg-miorosso text-white rounded-md transition-colors duration-300 hover:bg-mioblu focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-50 font-semibold mt-4"
          >
            Salva modifiche
          </button>
        </form>

        {message && (
          <p className={`mt-4 text-sm font-medium ${message.includes('Errore') ? 'text-red-500' : 'text-green-600'}`}>
            {message}
          </p>
        )}
      </div>
    </div>
  );
}
